<template>
  <div class="py-3 px-6" v-if="show">
    <label for="time_start" class="block text-sm font-medium leading-6 text-secondary">Fra tidspunkt</label>
    <div>
      <input id="time_start" name="time_start" type="time" v-model="fromTime" required
        @blur="fromTimeTouched = true"
        class="peer block w-full rounded-md border-0 pb-1.5 pt-2 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-200 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-secondary sm:text-sm sm:leading-6 font-medium" />
      <span v-if="fromTimeTouched && !fromTime" class="m-2 hidden text-sm text-error peer-[&:not(:focus):invalid]:block">Angiv et gyldigt tidspunkt.</span>
    </div>
  </div>

  <div class="py-2 px-6" v-if="show">
    <label for="time_end" class="block text-sm font-medium leading-6 text-secondary">Til tidspunkt</label>
    <div>
      <input id="time_end" name="time_end" type="time" v-model="toTime" required
        @blur="toTimeTouched = true"
        class="peer block w-full rounded-md border-0 pb-1.5 pt-2 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-200 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-secondary sm:text-sm sm:leading-6 font-medium" />
      <span v-if="toTimeTouched && !toTime" class="m-2 hidden text-sm text-error peer-[&:not(:focus):invalid]:block">Angiv et gyldigt tidspunkt.</span>
    </div>
  </div>
  <div class="py-3 px-6" v-if="show">
    <div class="block text-sm font-medium leading-6 text-secondary">Tid registreret på dag <span v-if="gapMinutes !== 0">(pause fratrækkes total tid)</span></div>
    <div class="text-2xl font-medium leading-8 text-gray-800">{{ timeSpentWithGap }}</div>
  </div>
</template>

<script lang='ts'>
import { parseTime } from '@/core/utils'
import { computed, defineComponent, nextTick, onMounted, ref, watch } from 'vue'
import { useStore } from 'vuex'

export default defineComponent({
  name: 'time-interval-selector',
  setup () {
    const defaultTime = '0,00'
    const store = useStore()
    const fromTime = ref('')
    const toTime = ref('')
    const timeSpent = ref(defaultTime)
    const timeSpentWithGap = ref(defaultTime)
    const show = ref(false)
    const gapMinutes = ref(0)

    const fromTimeTouched = ref(false)
    const toTimeTouched = ref(false)

    onMounted(async () => {
      await nextTick()
      timeSpent.value = defaultTime
      timeSpentWithGap.value = defaultTime
    })

    const getTimeSpan = (toTotalMinutes: number, fromTotalMinutes: number, gapTimeMinutes: number) => {
      let adjustedToTotalMinutes = toTotalMinutes;

      // #60 - Handle overnight work by rolling over to next day
      if (adjustedToTotalMinutes < fromTotalMinutes) {
        adjustedToTotalMinutes += 1440 // Add 24 hours in minutes
      }

      let diff = (adjustedToTotalMinutes - gapTimeMinutes) - fromTotalMinutes
      if (diff < 0) {
        diff = 0;
      }
      const hours = Math.floor(diff / 60)
      const minutes = Math.trunc((diff % 60) * (100 / 60))
      const formattedMinutes = String(minutes).padStart(2, '0')
      return `${hours},${formattedMinutes}`
    }

    watch([fromTime, toTime], () => {
      if (fromTime.value && toTime.value) {
        const { hours: fromHours, minutes: fromMinutes } = parseTime(fromTime.value)
        const { hours: toHours, minutes: toMinutes } = parseTime(toTime.value)
        const fromTotalMinutes = fromHours * 60 + fromMinutes
        const toTotalMinutes = toHours * 60 + toMinutes

        timeSpent.value = getTimeSpan(toTotalMinutes, fromTotalMinutes, 0)
        timeSpentWithGap.value = getTimeSpan(toTotalMinutes, fromTotalMinutes, Number(gapMinutes.value))

        store.commit('report/UPDATE_REPORT', { key: 'timeFrom', value: fromTime.value })
        store.commit('report/UPDATE_REPORT', { key: 'timeTo', value: toTime.value })
        store.commit('report/UPDATE_REPORT', { key: 'timeSpent', value: timeSpent.value })
        store.commit('report/UPDATE_REPORT', { key: 'timeSpentWithGap', value: timeSpentWithGap.value })
      } else if ((fromTimeTouched.value && !fromTime.value) || (toTimeTouched.value && !toTime.value)) {
        store.commit('report/UPDATE_REPORT', { key: 'timeFrom', value: undefined })
        store.commit('report/UPDATE_REPORT', { key: 'timeTo', value: undefined })
        store.commit('report/UPDATE_REPORT', { key: 'timeSpent', value: undefined })
        store.commit('report/UPDATE_REPORT', { key: 'timeSpentWithGap', value: undefined })
        timeSpent.value = defaultTime
        timeSpentWithGap.value = defaultTime
      }
    }, { immediate: true })

    watch(() => store.state.report.currentReport, (currentReport) => {
      show.value = currentReport && currentReport.state !== 'FREE'
      gapMinutes.value = currentReport ? Number(currentReport.gapMinutes) : 0
      fromTime.value = ''
      toTime.value = ''
      timeSpent.value = defaultTime
      timeSpentWithGap.value = defaultTime

      if (currentReport) {
        if (currentReport.timeFrom) {
          fromTime.value = currentReport.timeFrom
        }
        if (currentReport.timeTo) {
          toTime.value = currentReport.timeTo
        }

        if (currentReport.timeFrom && currentReport.timeTo) {
          const { hours: fromHours, minutes: fromMinutes } = parseTime(currentReport.timeFrom)
          const { hours: toHours, minutes: toMinutes } = parseTime(currentReport.timeTo)
          const fromTotalMinutes = fromHours * 60 + fromMinutes
          const toTotalMinutes = toHours * 60 + toMinutes
          timeSpent.value = getTimeSpan(toTotalMinutes, fromTotalMinutes, 0)
          timeSpentWithGap.value = getTimeSpan(toTotalMinutes, fromTotalMinutes, Number(gapMinutes.value))
        }
      }
    }, { immediate: true })

    return { fromTime, toTime, timeSpent, fromTimeTouched, toTimeTouched, show, gapMinutes, timeSpentWithGap }
  }
})
</script>
